import axios from 'axios';
import toast from 'react-hot-toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
});

// Request interceptor to add token
apiClient.interceptors.request.use(
  (config) => {
    const isAdminPath = window.location.pathname.startsWith('/admin');
    config.headers['X-Client-Platform'] = config.headers['X-Client-Platform'] || 'web';
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz && !config.headers['X-Timezone']) {
        config.headers['X-Timezone'] = tz;
      }
    } catch (e) {}
    
    // Only set Content-Type to application/json if data is not FormData
    if (!(config.data instanceof FormData)) {
      config.headers['Content-Type'] = 'application/json';
    }
    
    // For admin paths, prioritize adminToken. For other paths, prioritize accessToken.
    let token;
    if (isAdminPath) {
      token = localStorage.getItem('adminToken') || localStorage.getItem('accessToken');
    } else {
      token = localStorage.getItem('accessToken') || localStorage.getItem('adminToken');
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const currentPath = window.location.pathname;
      const isAdminPath = currentPath.startsWith('/admin');
      
      // Don't redirect if we're already on a login or signup page
      const isAuthPage = currentPath === '/login' || 
                        currentPath === '/register' || 
                        currentPath === '/login/admin' || 
                        currentPath === '/signup/admin';

      if (!isAuthPage) {
        // Clear all potential auth data
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminUser');
        
        // Redirect based on current path
        if (isAdminPath) {
          window.location.href = '/admin/login';
        } else {
          window.location.href = '/login';
        }
        
        toast.error('Session expired. Please login again.');
      }
    }

    const status = error.response?.status;
    const data = error.response?.data;

    let message;
    if (status === 413) {
      if (data && typeof data === 'object' && typeof data.message === 'string') {
        message = data.message;
      } else {
        message = 'Upload too large for server. Try a smaller file or increase the server upload limit.';
      }
    } else if (data && typeof data === 'object' && typeof data.message === 'string') {
      message = data.message;
    } else if (typeof data === 'string') {
      if (status === 413 || data.toLowerCase().includes('request entity too large')) {
        message = 'Upload too large for server. Try a smaller file or increase the server upload limit.';
      } else {
        message = error.message || 'An error occurred';
      }
    } else {
      message = error.message || 'An error occurred';
    }

    toast.error(message);
    
    return Promise.reject(error);
  }
);

export default apiClient;
